import * as THREE from "three";
import { getDisplayDebugInfo, isMobileDevice } from "../utils/device_utils.js";
import { SceneDimmer } from "./scene_dimmer.js";
import { SceneManager } from "./scene_manager.js";
import { MeshBatcher } from "./mesh_batcher.js";
import { ShadowBatcher } from "./shadow_batcher.js";

export class Renderer
{
  private static readonly DESKTOP_MAX_WIDTH_TO_HEIGHT = 0.75;

  static renderer: THREE.WebGLRenderer;
  static clock = new THREE.Clock();

  private static canvas_bounds: DOMRect | null = null;
  private static buffer_released = false;

  static setBufferReleased( released: boolean ): void
  {
    if ( !this.renderer || this.buffer_released === released )
      return;
    this.buffer_released = released;
    if ( released )
      this.releaseDrawingBuffer();
    else
      this.resize();
  }

  private static releaseDrawingBuffer(): void
  {
    const canvas = this.renderer.domElement;
    canvas.width = 1;
    canvas.height = 1;
  }

  static requestShadowUpdate(): void
  {
    this.renderer.shadowMap.needsUpdate = true;
  }

  static getCanvasBounds(): DOMRect
  {
    if ( !this.canvas_bounds )
      this.canvas_bounds = this.renderer.domElement.getBoundingClientRect();
    return this.canvas_bounds;
  }

  private static keepPrograms(): void
  {
    const programs = this.renderer.info.programs;
    if ( !programs )
      return;

    const push = programs.push;
    programs.push = function ( ...added )
    {
      for ( const program of added )
        program.usedTimes++;
      return push.apply( this, added );
    };
  }

  static setDiagnosticPixelRatio( ratio: number ): void
  {
    this.renderer.setPixelRatio( ratio );
    this.resize();
  }

  static init()
  {
    this.renderer = new THREE.WebGLRenderer( {
      antialias: localStorage.getItem( "perf_diag_no_msaa" ) !== "1"
    } );

    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    // Rebuilding the shadow map costs ~4ms per frame here. GameScreen requests
    // it only while something can actually move.
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.shadowMap.needsUpdate = true;
    this.renderer.localClippingEnabled = true;

    this.renderer.setPixelRatio( window.devicePixelRatio );
    this.keepPrograms();
    ShadowBatcher.install( this.renderer );

    ( window as any ).__renderer = this.renderer;

    document.body.appendChild(
      this.renderer.domElement
    );

    this.resize();

    window.addEventListener( "resize", () =>
    {
      this.resize();
    } );
    window.visualViewport?.addEventListener( "resize", () =>
    {
      this.resize();
    } );
  }

  // Public so Game.handleViewportResize can re-run it after it has updated the
  // menuCanvas reference style — the window "resize" event fires before those
  // styles change, so the listener below alone would size to stale values.
  public static resize(): void
  {
    const reference = document.getElementById( "menuCanvas" ) as HTMLCanvasElement | null;
    const reference_width = Number.parseFloat( reference?.style.width ?? "" );
    const reference_height = Number.parseFloat( reference?.style.height ?? "" );
    const reference_left = Number.parseFloat( reference?.style.left ?? "" );
    const reference_top = Number.parseFloat( reference?.style.top ?? "" );

    const viewport_width = window.innerWidth;
    const fallback_height = window.innerHeight;
    const fallback_width = isMobileDevice()
      ? viewport_width
      : Math.min( viewport_width, Math.round( fallback_height * this.DESKTOP_MAX_WIDTH_TO_HEIGHT ) );
    const width = Number.isFinite( reference_width ) ? Math.round( reference_width ) : fallback_width;
    const height = Number.isFinite( reference_height ) ? Math.round( reference_height ) : fallback_height;
    const left = Number.isFinite( reference_left ) ? Math.round( reference_left ) : Math.round( ( viewport_width - width ) * 0.5 );
    const top = Number.isFinite( reference_top ) ? Math.round( reference_top ) : 0;

    this.renderer.setSize( width, height );
    if ( this.buffer_released )
      this.releaseDrawingBuffer();
    this.renderer.domElement.style.left = `${ left }px`;
    this.renderer.domElement.style.top = `${ top }px`;
    this.canvas_bounds = null;

    // The camera's projection matrix is otherwise frozen at whatever aspect it
    // had on construction. Real mobile browsers (iOS Safari's address bar/
    // toolbar collapsing, on-screen keyboard, orientation change) resize the
    // viewport AFTER load, so without this the 3D scene drifts out of sync
    // with the canvas — world-space objects (e.g. the cannon) end up looking
    // shifted from where they should render.
    if ( SceneManager.camera )
    {
      SceneManager.camera.aspect = width / height;
      SceneManager.camera.updateProjectionMatrix();
    }

    const debug = {
      ...getDisplayDebugInfo(),
      selectedMode: isMobileDevice() ? "mobile-fullscreen" : "desktop-3:4",
      rendererCssWidth: width,
      rendererCssHeight: height,
      rendererLeft: left,
      rendererDrawingBufferWidth: this.renderer.domElement.width,
      rendererDrawingBufferHeight: this.renderer.domElement.height,
    };
    ( window as any ).__displayDebug = {
      ...( ( window as any ).__displayDebug ?? {} ),
      renderer: debug,
    };
    console.info( "[DisplayDebug][Renderer]", debug );
  }

  private static performRenderPass(
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera
  ): void
  {
    if ( !SceneDimmer.isActive() )
    {
      this.renderer.render( scene, camera );
      return;
    }

    const camera_mask = camera.layers.mask;
    const background = scene.background;
    const shadow_needs_update = this.renderer.shadowMap.needsUpdate;

    this.renderer.shadowMap.needsUpdate = false;
    camera.layers.set( SceneDimmer.BACKGROUND_LAYER );
    this.renderer.render( scene, camera );

    this.renderer.autoClear = false;
    SceneDimmer.renderVeil( this.renderer );

    scene.background = null;
    SceneManager.sky.visible = false;
    camera.layers.mask = ~( ( 1 << SceneDimmer.BACKGROUND_LAYER ) | MeshBatcher.HIDDEN_MASK ) >>> 0;
    this.renderer.shadowMap.needsUpdate = shadow_needs_update;
    this.renderer.render( scene, camera );

    scene.background = background;
    SceneManager.sky.visible = true;
    camera.layers.mask = camera_mask;
    this.renderer.autoClear = true;
  }

  public static render(
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera
  ): void
  {
    this.renderer.setRenderTarget( null );
    MeshBatcher.sync( scene, camera );
    this.renderer.info.render.frame++;
    const auto_update = scene.matrixWorldAutoUpdate;
    scene.matrixWorldAutoUpdate = false;
    this.performRenderPass( scene, camera );
    scene.matrixWorldAutoUpdate = auto_update;
  }

  public static compile( scene: THREE.Scene, camera: THREE.Camera ): void
  {
    MeshBatcher.sync( scene, camera );
    this.renderer.compile( scene, camera );
  }
}
